/* Where the API and the sign-in service are.
 *
 * THREE CASES, IN ORDER OF PRECEDENCE.
 *
 * 1. An explicit build-time URL. Production sets both, because there the API
 *    is a real host with a real certificate and nothing should be inferred.
 *
 * 2. Served over https, which locally means behind the demo proxy: everything
 *    is SAME-ORIGIN under a path. One origin, one certificate, no CORS and no
 *    preflight. That is not a preference, it is the fix: talking to three
 *    origins meant three certificates to be trusted and a CORS negotiation on
 *    every call, and both of those failed on Safari while passing in Chrome.
 *
 * 3. Plain http, which means Vite is being hit directly during development.
 *    The services are on their own ports and CORS is exercised, which is worth
 *    keeping so the production path is not only tested by its unit tests.
 *
 * Derived from the page's own address rather than hard-coded to localhost,
 * because a phone on the office wifi cannot reach the laptop's localhost.
 */
const loc = typeof window === "undefined" ? null : window.location;
const host = loc?.hostname ?? "localhost";
const origin = loc?.origin ?? "http://localhost";
const secure = loc?.protocol === "https:";

export const API_URL =
  import.meta.env.VITE_API_URL ??
  (secure ? `${origin}/api/v1` : `http://${host}:8000/v1`);

export const AUTH_URL =
  import.meta.env.VITE_AUTH_URL ??
  (secure ? `${origin}/auth` : `http://${host}:9999`);

/* Which channel carries the sign-in code.
 *
 * A CONFIGURATION VALUE BECAUSE THE ANSWER WILL CHANGE. SMS to Qatar costs
 * 0.2634 USD a message. At a merchant subscription near QAR 150 a month, about
 * 158 messages consume the whole subscription, so the pilot can afford SMS and
 * the business cannot.
 *
 * The seam is GoTrue's and it already exists: `/otp` takes a `channel` and the
 * deployed version answers an unknown one with "supported values are 'sms' or
 * 'whatsapp'". Switching is this value plus GOTRUE_SMS_PROVIDER, not a
 * rewrite.
 *
 * It stays "sms" until the WhatsApp Business account exists and a real message
 * has been delivered over it. Setting it earlier would be choosing a channel
 * on the strength of a price nobody has quoted yet. */
export const OTP_CHANNEL = (import.meta.env.VITE_OTP_CHANNEL as "sms" | "whatsapp") ?? "sms";

/* Which ways in the first screen offers.
 *
 * THE ORDER OF THIS LIST IS THE ORDER ON THE SCREEN, and the artboard's order
 * is apple, google, microsoft, then phone, then email.
 *
 * IT IS STILL A CONFIGURATION VALUE, AND THE REASON CHANGED. It used to be one
 * because Apple and Google were "configuration until a real customer is
 * watched". Now it is one because each provider needs a client registered with
 * that provider, and a button whose provider is not registered sends a
 * customer to an error page on somebody else's domain -- which is a worse dead
 * end than an absent button, because it looks like our fault and happens after
 * they have already committed.
 *
 * SO THIS LIST IS ORDER, NOT AVAILABILITY, and those are deliberately two
 * different things held in two different places. This value says which routes
 * the screen knows about and in what order; GoTrue's /settings says which of
 * them actually exist. Making this list the on/off switch as well would be a
 * second copy of a fact the auth service already owns, and the way that copy
 * goes wrong is a button whose provider refuses it.
 *
 * The default is the artboard's own order, less Microsoft.
 *
 * NO MICROSOFT FOR CUSTOMERS (ruled 2026-09-18). A Microsoft sign-in asserts no
 * verified address, so it can never claim the card a till made for somebody: a
 * customer who chose it would get a card they could never reach, and nothing on
 * this screen would say so. It is not in this list and cannot be put back by
 * configuration: the filter below admits no such id. Microsoft stays on the
 * staff side, where the tenant is Damga's.
 */
export const SIGN_IN_METHODS = (
  (import.meta.env.VITE_SIGN_IN_METHODS as string | undefined)
  ?? "apple,google,phone,email"
)
  .split(",")
  .map((s) => s.trim())
  .filter((s): s is "phone" | "email" | "apple" | "google" =>
    s === "phone" || s === "email" || s === "apple" || s === "google",
  );
